import { defineStore } from 'pinia'
import Cookies from 'js-cookie'
import { computed, reactive, toRefs } from 'vue'
import { useRouter } from 'vue-router'
import { localePath } from '@/utils/localePath.js'
import { useLanguageStore } from '@/store/language.js'
import { appConfig } from '@/configs/appConfig.js'
import { PROFILE_ACTIVE_STATUSES } from '@/constants/profile.constants.js'
import { i18n } from '@/i18n/index.js'
import dayjs from 'dayjs'
import { saveFullUser, loadFullUser, removeFullUser } from '@/utils/userStorage.js'

export const TOKEN_KEY = 'auth._token.local'
export const TOKEN_EXPIRATION_KEY = 'auth._token_expires_at.local'
export const USER_KEY = 'talent.user' // compact
export const AUTH_STRATEGY_KEY = 'auth.strategy'
export const AUTH_TOKEN_EXPIRATION_LOCAL = 'auth._token_expiration.local'

function getLocaleFromUrl() {
  const parts = window.location.pathname.split('/')
  return parts[1] || null
}

// compact user only for cookie / external sync
function compactUser(u) {
  return {
    slug: u.slug,
    name: u.name,
    avatar: u.avatar || null,
    locale: u.locale
  }
}

export const useAuthStore = defineStore('auth', () => {
  let api = null
  const router = useRouter()
  const languageStore = useLanguageStore()

  const state = reactive({
    locale: (() => {
      if (window?.location?.pathname?.includes('/hidden/profile-rri/pdf/')) {
        return getLocaleFromUrl() || appConfig.defaultLocale
      }
      return Cookies.get('locale') || appConfig.defaultLocale
    })(),
    user: loadFullUser(), // LOAD FULL USER
    loading: false,
    error: null
  })

  const isAuthenticated = () => {
    const token = Cookies.get(TOKEN_KEY)
    return !!token && token !== 'false'
  }
  const getUser = computed(() => state.user)
  const getToken = () => Cookies.get(TOKEN_KEY) || null
  const getLocale = computed(() => state.locale)

  // Candidate profile shortcuts (user.payload.profile)
  const getProfile = computed(() => state.user?.payload?.profile ?? null)
  const getProfileSlug = computed(() => getProfile.value?.slug ?? null)
  const getProfileStatus = computed(() => getProfile.value?.status ?? null)
  const isProfileActive = computed(() => PROFILE_ACTIVE_STATUSES.includes(getProfileStatus.value))

  const setApi = (apiInstance) => {
    api = apiInstance
  }

  const setLocale = (locale) => {
    state.locale = locale
    Cookies.set('locale', locale)

    if (i18n.global.locale.value !== locale) {
      i18n.global.locale.value = locale
    }

    const languageStore = useLanguageStore()
    if (languageStore.currentLocale !== locale) {
      languageStore.setLocale(locale)
    }

    document.documentElement.setAttribute('lang', locale)
  }

  const login = async (payload) => {
    state.loading = true
    state.error = null
    try {
      await api.account.csrf?.()
      const res = await api.user.login(payload)
      const token = res.data?.data?.token

      if (token !== null) {
        const expiresAt = dayjs(res.data?.data?.expires_at).toDate()
        Cookies.set(TOKEN_KEY, `Bearer ${token}`, { expires: expiresAt })
        Cookies.set(TOKEN_EXPIRATION_KEY, expiresAt.toISOString(), { expires: expiresAt })
      }

      return res
    } catch (e) {
      state.error = e.response?.data?.message || 'Login failed'
    } finally {
      state.loading = false
    }
  }

  const fetchUser = async () => {
    try {
      if (!isAuthenticated()) return false

      const { data } = await api.user.me()
      const expiresAt = dayjs(Cookies.get(TOKEN_EXPIRATION_KEY)).toDate()

      state.user = data.data

      // sync locale
      setLocale(data.data.locale)

      Cookies.set(
        USER_KEY,
        JSON.stringify(compactUser(state.user)),
        { expires: expiresAt, secure: true, sameSite: 'none' }
      )

      // write FULL to localStorage
      saveFullUser(state.user, expiresAt)

      // notify parent if opened via popup
      window?.opener?.postMessage({ type: 'talent-sync', data: compactUser(state.user) }, '*')
    } catch (e) {
      state.error = e.response?.data?.message || 'Fetch user failed'
      if (e.response?.data?.message === 'Unauthenticated.') {
        removeSession()
        await router.push(localePath('/login', languageStore.currentLocale))
      }
    }
  }

  const logout = async () => {
    await api.user.logout().catch(() => {})
    removeSession()
    window?.opener?.postMessage({ type: 'talent-unsynced' }, '*')
    await router.push(localePath('/login', languageStore.currentLocale))
  }

  const removeSession = () => {
    Cookies.remove(TOKEN_KEY)
    Cookies.remove(TOKEN_EXPIRATION_KEY)
    Cookies.remove(USER_KEY)
    Cookies.remove(AUTH_STRATEGY_KEY)
    Cookies.remove(AUTH_TOKEN_EXPIRATION_LOCAL)
    removeFullUser()
    state.user = null
  }

  const setUser = (payload) => {
    state.user = payload
    saveFullUser(payload, 30)
    Cookies.set(
      USER_KEY,
      JSON.stringify(compactUser(state.user)),
      { expires: expiresAt, secure: true, sameSite: 'none' }
    )
  }

  const setUserToken = (token) => {
    Cookies.set(TOKEN_KEY, `Bearer ${token}`, { expires: 30 })
  }

  return {
    ...toRefs(state),
    isAuthenticated,
    getUser,
    getToken,
    getLocale,
    getProfile,
    getProfileSlug,
    getProfileStatus,
    isProfileActive,
    setApi,
    setLocale,
    login,
    logout,
    fetchUser,
    setUser,
    setUserToken
  }
})
